import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Chip, Paper, Button, Collapse, IconButton,
  LinearProgress, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Finding, AreaResult } from '../../services/customsAuditService';

interface AppliedFix {
  originalValue: string;
  newValue: string;
}

interface AuditFindingsProps {
  areaResults: AreaResult[];
  appliedFixes: Map<string, AppliedFix>;
  onApplyFix: (field: string, value: string) => void;
  onUndoFix: (field: string) => void;
  onViewField: (field: string) => void;
  expandedArea?: string;
}

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITY_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  critical: { color: '#d32f2f', bg: '#ffebee', label: 'Critical' },
  high: { color: '#f57c00', bg: '#fff3e0', label: 'High' },
  medium: { color: '#fbc02d', bg: '#fffde7', label: 'Medium' },
  low: { color: '#1976d2', bg: '#e3f2fd', label: 'Low' },
};

const AuditFindings: React.FC<AuditFindingsProps> = ({
  areaResults, appliedFixes, onApplyFix, onUndoFix, onViewField, expandedArea,
}) => {
  const theme = useTheme();
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(
    new Set(expandedArea ? [expandedArea] : [])
  );

  const toggleArea = (area: string) => {
    setExpandedAreas(prev => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area); else next.add(area);
      return next;
    });
  };

  const filteredResults = useMemo(() => {
    return areaResults.map(ar => ({
      ...ar,
      findings: ar.findings
        .filter(f => !severityFilter || f.severity === severityFilter)
        .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)),
    })).filter(ar => ar.findings.length > 0);
  }, [areaResults, severityFilter]);

  const renderFinding = (f: Finding, idx: number) => {
    const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.medium;
    const isApplied = f.field ? appliedFixes.has(f.field) : false;
    const hasFix = f.suggested_fix && f.field;

    return (
      <Paper
        key={`${f.code}-${idx}`}
        sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: isApplied ? 'success.light' : 'divider',
              bgcolor: isApplied ? alpha(theme.palette.success.main, 0.04) : 'background.paper',
              borderRadius: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Chip label={sev.label} size="small"
            sx={{ bgcolor: sev.bg, color: sev.color, fontWeight: 600, fontSize: 11 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500}>{f.message}</Typography>
            {f.field && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                Field: {f.field}
              </Typography>
            )}
            {hasFix && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Current: <b>{f.actual || '(empty)'}</b> → Suggested: <b>{f.suggested_fix}</b>
                </Typography>
                {f.fix_confidence != null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <LinearProgress
                      variant="determinate" value={f.fix_confidence * 100}
                      sx={{ flex: 1, height: 6, borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { bgcolor: f.fix_confidence >= 0.8 ? 'success.main' : f.fix_confidence >= 0.6 ? 'warning.main' : 'error.main' }
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {Math.round(f.fix_confidence * 100)}%
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {(hasFix || f.field) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
            {f.field && (
              <Button size="small" variant="text" startIcon={<VisibilityIcon />}
                onClick={() => onViewField(f.field!)}>
                View Field
              </Button>
            )}
            {hasFix && !isApplied && (
              <Button size="small" variant="contained" color="primary"
                onClick={() => onApplyFix(f.field!, f.suggested_fix!)}>
                Apply Fix
              </Button>
            )}
            {isApplied && (
              <>
                <Chip icon={<CheckCircleIcon />} label="Applied" color="success" size="small" />
                <Tooltip title="Undo fix">
                  <IconButton size="small" onClick={() => onUndoFix(f.field!)}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={severityFilter} exclusive size="small"
          onChange={(_, v) => setSeverityFilter(v)}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="critical" sx={{ color: '#d32f2f' }}>Critical</ToggleButton>
          <ToggleButton value="high" sx={{ color: '#f57c00' }}>High</ToggleButton>
          <ToggleButton value="medium" sx={{ color: '#fbc02d' }}>Medium</ToggleButton>
          <ToggleButton value="low" sx={{ color: '#1976d2' }}>Low</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredResults.map(ar => (
        <Paper key={ar.area} sx={{ mb: 1.5, overflow: 'hidden', borderRadius: 1.5 }}>
          <Box
            onClick={() => toggleArea(ar.area)}
            sx={{ display: 'flex', alignItems: 'center', p: 1.5, cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, textTransform: 'capitalize' }}>
              {ar.area.replace('_', ' ')}
            </Typography>
            <Chip label={`${ar.findings.length}`} size="small" color={ar.status === 'pass' ? 'success' : ar.status === 'warning' ? 'warning' : 'error'} sx={{ mr: 1 }} />
            <ExpandMoreIcon sx={{ transform: expandedAreas.has(ar.area) ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
          </Box>
          <Collapse in={expandedAreas.has(ar.area)}>
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              {ar.findings.map((f, i) => renderFinding(f, i))}
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );
};

export default AuditFindings;
